import * as dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { TimeZone } from '../../time/timezone.js';
import { TrueTime } from '../../time/true-time.js';
import { VersionRange } from '../core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { ISingleUseRedeemableCode, RedeemableCode, RedeemableCodeStatus } from '../redeemable-code.js';
import { BusinessModuleRef } from './business-module-ref.js';

export class ClientInviteCode extends RedeemableCode implements ISingleUseRedeemableCode {
  static readonly TYPE_ID: string = '1effe3fa00ac41608a811350fdec2282';
  static readonly SCHEMA_VERSION: number = 6;

  public readonly status: RedeemableCodeStatus;

  constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: dayjs.Dayjs,
    public readonly expirationDate: dayjs.Dayjs | null,
    public readonly ownerStaffId: string | null,
    public readonly usedBy: string | null,
    public readonly usedAt: dayjs.Dayjs | null,
    status: RedeemableCodeStatus | null = null,
  ) {
    super();

    this.status = status ?? RedeemableCodeStatus.Live;
  }

  get businessId(): string {
    return this.businessRef.uniqueId;
  }

  get hasBeenUsed(): boolean {
    return this.status !== RedeemableCodeStatus.Live;
  }

  get hasExpired(): boolean {
    if(isNullOrUndefined(this.expirationDate)) {
      return false;
    }

    return TrueTime.now().isAfter(this.expirationDate);
  }

  get $typeId() {
    return ClientInviteCode.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(ClientInviteCode.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ClientInviteCode {
    return new ClientInviteCode(
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'expirationDate')),
        MapUtils.getOrNull<string>(map, 'ownerStaffId'),
        MapUtils.getOrNull<string>(map, 'usedBy'),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'usedAt')),
        toEnumFromStringOrNull(MapUtils.get<string>(map, '$status'), RedeemableCodeStatus),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'expirationDate': context.encodeDateOrNull(this.expirationDate),
      'ownerStaffId': this.ownerStaffId,
      'usedBy': this.usedBy,
      'usedAt': context.encodeDateOrNull(this.usedAt),
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return new ClientInviteCode(
        this.uniqueId,
        this.businessRef.clone(),
        this.creationDate,
        this.expirationDate,
        this.ownerStaffId,
        this.usedBy,
        TimeZone.cloneDateOrNull(this.usedAt),
        this.status,
    ) as this;
  }

  isSame(other: ClientInviteCode): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      isSameData(other.businessRef, this.businessRef) &&
      other.creationDate === this.creationDate &&
      other.expirationDate === this.expirationDate &&
      other.ownerStaffId === this.ownerStaffId &&
      other.usedBy === this.usedBy &&
      other.usedAt === this.usedAt &&
      other.status === this.status;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new ClientInviteCode(
        this.uniqueId,
        businessRef.clone(),
        this.creationDate,
        this.expirationDate,
        this.ownerStaffId,
        this.usedBy,
        TimeZone.cloneDateOrNull(this.usedAt),
        this.status,
    ) as this;
  }
}

export class ClientInviteCodeBuilder extends DataModelBuilder<ClientInviteCode> {
  build(context: SerializationContext, map: SerializedData): ClientInviteCode {
    return ClientInviteCode.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ClientInviteCode.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ClientInviteCode.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2 adds the [$status] field.
    if(version < 2) {
      MapUtils.set(map, '$status', RedeemableCodeStatus.Live);
      version = 2;
    }

    // Version 3 adds the [usedBy] and [usedAt] fields.
    if(version < 3) {
      version = 3;
    }

    // Version 4 adds the [ownerStaffId] field.
    if(version < 4) {
      version = 4;
    }

    // Version 5 adds the [clientProfileId] field.
    if(version < 5) {
      version = 5;
    }

    // Version 6i:
    // - removes the [clientProfileId] field
    // - removes the [businessId] field
    // - adds the [businessRef] field.
    if(version < 6) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
